import { EXPENSE_GROUP_LABELS, type ExpenseGroup } from './catalog'
import type { FinanceLineRow } from './types'

export type StatementLine = {
  code: string
  label: string
  group?: string
  actual: number
  budget: number
  priorYear: number
  varianceBudget: number     // actual - budget (revenu) ou budget - actual (dépense)
  variancePriorYear: number  // actual - priorYear (revenu) ou priorYear - actual (dépense)
  pctVsBudget: number        // ratio 0..1 ; favorable lorsque positif
  pctVsPriorYear: number
}

export type Statement = {
  revenueLines: StatementLine[]
  expenseLines: StatementLine[]
  totalRevenue: StatementLine
  totalExpense: StatementLine
  netIncome:    StatementLine
}

// Variance/% sont normalisés "favorable = positif".
// - Pour un revenu : favorable = actual > budget → variance = actual - budget
// - Pour une dépense : favorable = actual < budget → variance = budget - actual
export function computeStatement(lines: FinanceLineRow[]): Statement {
  const revenueRows = lines.filter((l) => l.section === 'revenue')
  const expenseRows = lines.filter((l) => l.section === 'expense')

  const revenueLines = revenueRows.map((l) => toStatementLine(l, 'revenue'))
  const expenseLines = expenseRows.map((l) => toStatementLine(l, 'expense'))

  const totalRevenue = aggregate('Total revenus', revenueLines, 'revenue')
  const totalExpense = aggregate('Total dépenses', expenseLines, 'expense')

  const netActual    = totalRevenue.actual    - totalExpense.actual
  const netBudget    = totalRevenue.budget    - totalExpense.budget
  const netPriorYear = totalRevenue.priorYear - totalExpense.priorYear

  // Bénéfice net : favorable = plus élevé (comme un revenu).
  const netIncome: StatementLine = {
    code: 'NET',
    label: 'Bénéfice net',
    actual: netActual,
    budget: netBudget,
    priorYear: netPriorYear,
    varianceBudget: netActual - netBudget,
    variancePriorYear: netActual - netPriorYear,
    pctVsBudget: ratio(netActual - netBudget, Math.abs(netBudget)),
    pctVsPriorYear: ratio(netActual - netPriorYear, Math.abs(netPriorYear)),
  }

  return { revenueLines, expenseLines, totalRevenue, totalExpense, netIncome }
}

function toStatementLine(
  l: FinanceLineRow,
  section: 'revenue' | 'expense',
): StatementLine {
  const actual    = Number(l.amount_actual)
  const budget    = Number(l.amount_budget)
  const priorYear = Number(l.amount_prior_year)

  const varianceBudget    = section === 'revenue' ? actual - budget    : budget    - actual
  const variancePriorYear = section === 'revenue' ? actual - priorYear : priorYear - actual

  return {
    code: l.code,
    label: l.label,
    group: l.group,
    actual,
    budget,
    priorYear,
    varianceBudget,
    variancePriorYear,
    pctVsBudget:    ratio(varianceBudget,    Math.abs(budget)),
    pctVsPriorYear: ratio(variancePriorYear, Math.abs(priorYear)),
  }
}

function aggregate(
  label: string,
  lines: StatementLine[],
  section: 'revenue' | 'expense',
): StatementLine {
  const actual    = lines.reduce((s, l) => s + l.actual,    0)
  const budget    = lines.reduce((s, l) => s + l.budget,    0)
  const priorYear = lines.reduce((s, l) => s + l.priorYear, 0)

  const varianceBudget    = section === 'revenue' ? actual - budget    : budget    - actual
  const variancePriorYear = section === 'revenue' ? actual - priorYear : priorYear - actual

  return {
    code: 'TOTAL',
    label,
    actual,
    budget,
    priorYear,
    varianceBudget,
    variancePriorYear,
    pctVsBudget:    ratio(varianceBudget,    Math.abs(budget)),
    pctVsPriorYear: ratio(variancePriorYear, Math.abs(priorYear)),
  }
}

function ratio(numerator: number, denominator: number): number {
  if (!denominator) return 0
  return numerator / denominator
}

// Agrège des lignes de dépenses par `group` (cogs / indirect_cogs / opex /
// other) en lignes synthétiques (code = clé de groupe). Utilisé par les cartes
// de répartition pour rester lisibles malgré ~23 lignes détaillées.
export function groupExpenseLines(lines: StatementLine[]): StatementLine[] {
  const order: ExpenseGroup[] = ['cogs', 'indirect_cogs', 'opex', 'other']
  const result: StatementLine[] = []
  for (const g of order) {
    const grp = lines.filter((l) => l.group === g)
    if (grp.length === 0) continue
    const actual = grp.reduce((s, l) => s + l.actual, 0)
    const budget = grp.reduce((s, l) => s + l.budget, 0)
    const priorYear = grp.reduce((s, l) => s + l.priorYear, 0)
    const varianceBudget = budget - actual
    const variancePriorYear = priorYear - actual
    result.push({
      code: g,
      label: EXPENSE_GROUP_LABELS[g],
      group: g,
      actual,
      budget,
      priorYear,
      varianceBudget,
      variancePriorYear,
      pctVsBudget: ratio(varianceBudget, Math.abs(budget)),
      pctVsPriorYear: ratio(variancePriorYear, Math.abs(priorYear)),
    })
  }
  return result
}

// Classes Tailwind selon le signe de la variance déjà normalisée
// (positif = favorable). Renvoie une chaîne vide quand la variance est nulle.
export function varianceClass(variance: number): string {
  if (!Number.isFinite(variance) || variance === 0) return 'text-muted-foreground'
  return variance > 0 ? 'text-emerald-700' : 'text-[var(--gs-red)]'
}

// ─── Projection annuelle des revenus ─────────────────────────────────────────
// Sert de dénominateur au ratio « masse salariale ÷ revenus » : les revenus
// réels ne couvrent que les mois saisis, on relaie avec le budget des mois
// restants (même logique que `isForecast` de getFinanceYearSeries).

export type YearRevenueOutlook = {
  actualMonths: number // mois « saisis » (0..12)
  revenueActualYtd: number
  revenueBudgetYear: number // budget des 12 mois
  revenueProjected: number // réel des mois saisis + budget des mois restants
}

// `months` = 12 entrées janv..déc { actual, budget } ; `actualMonths` = nombre
// de mois considérés comme réels (le dernier mois saisi pilote, cf. appelant).
export function projectYearRevenue(
  months: { actual: number; budget: number }[],
  actualMonths: number,
): YearRevenueOutlook {
  const cutoff = Math.min(Math.max(actualMonths, 0), months.length)
  const revenueActualYtd = months
    .slice(0, cutoff)
    .reduce((s, m) => s + m.actual, 0)
  const revenueBudgetYear = months.reduce((s, m) => s + m.budget, 0)
  const revenueProjected =
    revenueActualYtd +
    months.slice(cutoff).reduce((s, m) => s + m.budget, 0)
  return { actualMonths: cutoff, revenueActualYtd, revenueBudgetYear, revenueProjected }
}
